import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminApi, type RenewalItem, type RevenueTrendPoint } from '../../api/admin';
import { feedbackApi } from '../../api/feedback';

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

function RevenueTrend({ data }: { data: RevenueTrendPoint[] }) {
  const max = Math.max(...data.map(d => d.revenue), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, padding: '0.5rem 0' }}>
      {data.map(d => (
        <div key={d.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div
            style={{
              width: '100%', background: d.revenue > 0 ? 'var(--primary)' : 'var(--border)',
              borderRadius: '3px 3px 0 0', opacity: d.revenue > 0 ? 0.85 : 0.4,
              height: `${Math.max(4, (d.revenue / max) * 64)}px`,
              transition: 'height 0.3s',
            }}
            title={`${d.month}: ${fmt(d.revenue)}`}
          />
          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{d.month}</span>
        </div>
      ))}
    </div>
  );
}

function RenewalBadge({ days, expired }: { days: number; expired: boolean }) {
  if (expired) return <span className="badge badge-overdue">Expired</span>;
  if (days <= 7) return <span className="badge badge-overdue">{days}d left</span>;
  if (days <= 30) return <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>{days}d left</span>;
  return <span className="badge badge-gray">{days}d left</span>;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['admin-dashboard'], queryFn: adminApi.getDashboard, staleTime: 30 * 1000 });
  const { data: renewal = [] } = useQuery({ queryKey: ['admin-renewal-queue'], queryFn: adminApi.getRenewalQueue, staleTime: 60 * 1000 });
  const { data: trend = [] } = useQuery({ queryKey: ['admin-revenue-trend'], queryFn: () => adminApi.getRevenueTrend(12), staleTime: 5 * 60 * 1000 });
  const { data: feedback = [] } = useQuery({ queryKey: ['admin-feedback'], queryFn: feedbackApi.listAll, staleTime: 30 * 1000 });
  const [keyVisible, setKeyVisible] = useState(false);

  if (isLoading) return <div className="loading-overlay"><span className="spinner" /></div>;
  if (!data) return null;

  const now = new Date().toISOString().slice(0, 10);
  const urgentRenewals = (renewal as RenewalItem[]).filter(r => r.daysUntilExpiry <= 30 || r.isExpired).length;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">Admin Dashboard</h1></div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Clients', value: String(data.totalClients), icon: '👥', color: 'var(--primary)' },
          { label: 'Active Premium', value: String(data.activePremium), icon: '⭐', color: '#16a34a' },
          { label: 'Expiring in 30d', value: String(data.expiringSoon), icon: '⏳', color: '#d97706' },
          { label: 'Revenue This Month', value: fmt(data.revenueThisMonth), icon: '💰', color: 'var(--primary)' },
          { label: 'Total Revenue', value: fmt(data.totalRevenue), icon: '📈', color: '#7c3aed' },
        ].map(c => (
          <div key={c.label} className="card" style={{ padding: '1.1rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{c.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '1.4rem', marginTop: '0.25rem' }}>{c.icon}</div>
          </div>
        ))}
      </div>

      {/* Master key */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>🔑 Master Premium License Key</div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.4rem 0.75rem', letterSpacing: '0.1em', fontWeight: 700, fontSize: '1rem' }}>
            {keyVisible ? data.masterKey : '●●●●-●●●●-●●●●-●●●●-●●●●'}
          </code>
          <button className="btn btn-ghost btn-sm" onClick={() => setKeyVisible(v => !v)}>{keyVisible ? 'Hide' : 'Show'}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigator.clipboard.writeText(data.masterKey)}>Copy</button>
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
          This key unlocks premium features in the desktop app. All premium clients use this key.
        </p>
      </div>

      {/* Feedback requests */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div className="card-header">
          <span style={{ fontWeight: 700 }}>
            📬 Feedback Requests
            {feedback.filter(f => f.status === 'open').length > 0 && (
              <span className="badge badge-blue" style={{ marginLeft: 8 }}>{feedback.filter(f => f.status === 'open').length} open</span>
            )}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/feedback')}>View all →</button>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: 'var(--text-2)' }}>
          <span>✨ {feedback.filter(f => f.type === 'feature').length} feature</span>
          <span>🐛 {feedback.filter(f => f.type === 'bug').length} bug</span>
          <span>💬 {feedback.filter(f => f.type === 'other').length} general</span>
        </div>
      </div>

      {/* Revenue trend + Renewal queue */}
      <div className="form-row form-row-2" style={{ gap: '1.25rem' }}>
        <div className="card">
          <div className="card-header"><span style={{ fontWeight: 700 }}>📊 Monthly Revenue (12 months)</span></div>
          {trend.length > 0
            ? <RevenueTrend data={trend as RevenueTrendPoint[]} />
            : <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No revenue data yet</div>}
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)', paddingTop: '0.25rem' }}>
            Total: {fmt(trend.reduce((s, d) => s + (d as RevenueTrendPoint).revenue, 0))}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700 }}>
              🔔 Renewal Queue
              {urgentRenewals > 0 && <span className="badge badge-overdue" style={{ marginLeft: 8 }}>{urgentRenewals} urgent</span>}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/subscriptions')}>All →</button>
          </div>
          {(renewal as RenewalItem[]).length === 0
            ? <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No renewals due in 60 days</div>
            : (
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                <table>
                  <thead><tr><th>Client</th><th>Plan</th><th>Status</th><th>Phone</th></tr></thead>
                  <tbody>
                    {(renewal as RenewalItem[]).map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 500, fontSize: '0.83rem' }}>{r.client?.business ?? r.client?.name ?? '—'}</td>
                        <td><span className="badge badge-blue" style={{ fontSize: '0.7rem' }}>{r.plan}</span></td>
                        <td><RenewalBadge days={r.daysUntilExpiry} expired={r.isExpired} /></td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.client?.phone ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      <div className="form-row form-row-2" style={{ gap: '1.25rem', marginBottom: 0 }}>
        {/* Recent clients */}
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700 }}>Recent Clients</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/clients')}>View all →</button>
          </div>
          {data.recentClients.length === 0
            ? <div className="empty-state" style={{ padding: '1rem' }}><div className="empty-state-text">No clients yet</div></div>
            : (
              <table>
                <thead><tr><th>Business</th><th>Contact</th><th>City</th><th>Joined</th></tr></thead>
                <tbody>
                  {data.recentClients.map(c => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/admin/clients')}>
                      <td style={{ fontWeight: 600 }}>{c.business}</td>
                      <td style={{ color: 'var(--text-2)' }}>{c.name}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{c.city ?? '—'}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{c.createdAt.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>

        {/* Recent subscriptions */}
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 700 }}>Recent Payments</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/subscriptions')}>View all →</button>
          </div>
          {data.recentSubscriptions.length === 0
            ? <div className="empty-state" style={{ padding: '1rem' }}><div className="empty-state-text">No payments yet</div></div>
            : (
              <table>
                <thead><tr><th>Client</th><th>Plan</th><th style={{ textAlign: 'right' }}>Amount</th><th>Expires</th></tr></thead>
                <tbody>
                  {data.recentSubscriptions.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500 }}>{s.client?.business ?? '—'}</td>
                      <td><span className="badge badge-blue">{s.plan}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success)' }}>{fmt(s.amount)}</td>
                      <td style={{ fontSize: '0.83rem', color: s.expiresAt < now ? 'var(--danger)' : 'var(--text-2)' }}>{s.expiresAt.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}
